import i18next from 'i18next'
import * as React from 'react'
import {
  any,
  async,
  AsyncState,
  fromJSX,
  IOWidget,
  nothing,
  onlyIf,
  Updater,
  wait,
} from 'widgets-for-react'
import { UserSubscriptions } from '../../../subscription/subscriptionState'
import { TextPageHeader } from '../pageHeader/pageHeaderLayout'
import { paywallWidget } from '../paywall/paywallWidget'
import { ViewListenerState } from '../viewListener/viewListenerState'
import { SeasonalCalendarLayout } from './seasonalCalendarLayout'
import {
  SeasonalCalendarData,
  SeasonalCalendarState,
  seasonalCalendarStateUpdaters,
} from './seasonalCalendarState'

export const seasonalCalendarWidget =
  (
    isLoggedIn: boolean,
    userSubscriptionsState: AsyncState<UserSubscriptions>,
    view: ViewListenerState
  ): IOWidget<SeasonalCalendarState, Updater<SeasonalCalendarState>> =>
  (seasonalCalendarState) => {
    const isRendering =
      seasonalCalendarState.seasonalCalendarDataState.kind == 'loaded' &&
      seasonalCalendarState.renderUpTo <
        seasonalCalendarState.seasonalCalendarDataState.value.length
    return any([
      fromJSX(() => (
        <TextPageHeader
          title={i18next.t('seasonalCalendar:title')}
          intro={i18next.t('seasonalCalendar:intro')}
        />
      )),
      async<SeasonalCalendarData>(
        seasonalCalendarState.seasonalCalendarDataState
      ).map(seasonalCalendarStateUpdaters.updateSeasonalCalendarDataState),
      onlyIf(
        isRendering,
        wait(5)(
          () =>
            (x: number): number =>
              x + 1
        ).map(seasonalCalendarStateUpdaters.updateRenderUpTo)
      ),
      paywallWidget(isLoggedIn, userSubscriptionsState)(
        seasonalCalendarBaseWidget(
          view,
          true,
          isRendering
        )(seasonalCalendarState),
        seasonalCalendarBaseWidget(
          view,
          false,
          isRendering
        )(seasonalCalendarState)
      ),
    ])
  }

const seasonalCalendarBaseWidget =
  (
    view: ViewListenerState,
    paywalled: boolean,
    rendering: boolean
  ): IOWidget<SeasonalCalendarState, Updater<SeasonalCalendarState>> =>
  (seasonalCalendarState) => {
    const dataState = seasonalCalendarState.seasonalCalendarDataState
    return dataState.kind == 'unloaded'
      ? nothing()
      : fromJSX((setState) => (
          <SeasonalCalendarLayout
            seasonalCalendarData={
              dataState.kind == 'loaded'
                ? {
                    data: dataState.value
                      //batched rendering
                      .slice(0, seasonalCalendarState.renderUpTo)
                      //filter on selected month + show only the first three products of the first subcategory of the first main category when there is a paywall
                      .map((mainCat) => ({
                        ...mainCat,
                        subcategories: mainCat.subcategories
                          .map((subCat) => ({
                            ...subCat,
                            entries: subCat.entries
                              .filter((entry) =>
                                seasonalCalendarState.filterOnMonth !==
                                undefined
                                  ? [1, 2].includes(
                                      entry.MonthlyAvailabilities[
                                        seasonalCalendarState.filterOnMonth
                                      ]
                                    )
                                  : entry
                              )
                              .slice(0, paywalled ? 3 : subCat.entries.length),
                          }))
                          .filter((subCat) => subCat.entries.length > 0)
                          .slice(
                            0,
                            paywalled ? 1 : mainCat.subcategories.length
                          ),
                      }))
                      .filter((mainCat) => mainCat.subcategories.length > 0)
                      .slice(0, paywalled ? 1 : dataState.value.length),
                    showLoaderAsWell: rendering && !paywalled,
                  }
                : dataState.kind == 'loading'
                ? 'loading'
                : 'error'
            }
            expandedMainCategories={
              seasonalCalendarState.expandedMainCategories
            }
            updateExpandedMainCategories={(categories) =>
              setState(
                seasonalCalendarStateUpdaters.updateExpandedMainCategories(
                  categories
                )
              )
            }
            filterOnMonth={seasonalCalendarState.filterOnMonth}
            updateFilterOnMonth={(month) =>
              setState(seasonalCalendarStateUpdaters.updateFilterOnMonth(month))
            }
            view={view}
          />
        ))
  }
